import { Inject, Injectable } from '@angular/core';
import { ITagResult, IFullResource, ICommitChange } from 'app/interfaces/IResourceFilesProvider';
import { LoggerService } from 'app/logger/logger.service';
import { IFileClient } from 'app/services/interfaces/IFileClient';
import { IResourceBase, ResourceFilesUtils } from 'app/shared/utils/resourcefiles.utils';
import { ROOT_FOLDER } from './local-resource-provider.tokens';
import { existsSync, readdir, readFile } from 'fs';
import { promisify } from 'util';
import { convertObjectToString, joinPaths, normalizePath } from 'app/shared/utils/string.util';
import { ISaveResult } from 'shell-models-lib';
import { SaveStatus } from 'app/interfaces/ISaveStatus';

@Injectable()
export class LocalFileClientService implements IFileClient {
    protected static readonly className = 'LocalFileClientService';
    constructor(
        private readonly _loggerService: LoggerService,
        @Inject(ROOT_FOLDER) private readonly _rootFolder: string
    ) {
    }

    public async init(): Promise<boolean> {
        // No init process
        return true;
    }

    public async commit(commitChanges: ICommitChange[], message?: string): Promise<ISaveResult> {
        // TODO: Implement save logic. Currently no needed since we only read files.
        return {
            status: SaveStatus.Succeeded
        };
    }

    public getRootFolder(): string {
        return this._rootFolder;
    }

    public async getFilesInFolder(folderPath: string): Promise<ITagResult[]> {
        const promises: Array<Promise<ITagResult>> = [];
        const folderToScan = normalizePath(joinPaths(this._rootFolder, folderPath));
        let files: string[] = [];
        let fileResources: IFullResource[] = [];
        try {
            files = await this._getFolderFilesPath(folderToScan) || [];
            files.forEach((file) => promises.push(this._readFileAsResource(file, true)));
            fileResources = await Promise.all(promises) as IFullResource[];
        } catch (err) {
            this._loggerService.log(LocalFileClientService.className, `Unable to list files for: ${folderPath}, error: ${err}`);
        }
        return fileResources;
    }

    public async getFoldersInFolder(folderPath: string, isAbsolutePath?: boolean): Promise<IResourceBase[]> {
        const resolvedFolder = isAbsolutePath ? folderPath : normalizePath(joinPaths(this._rootFolder, folderPath));
        if (!existsSync(resolvedFolder)) {
            return [];
        }
        return (await promisify(readdir)(resolvedFolder, { encoding: 'utf8', withFileTypes: true }) || [])
            .filter((d) => d.isDirectory())
            .map((d) => {
                return {
                    name: d.name,
                    type: 'template'
                };
            });
    }

    public getFileContent(filePath: string, isAbsolutePath: boolean = false): Promise<string> {
        filePath = isAbsolutePath ? filePath : joinPaths(this._rootFolder, filePath);
        filePath = normalizePath(filePath);
        return new Promise((resolve, reject) => {
            readFile(filePath, 'utf8', (error, data) => {
                if (error) {
                    this._loggerService.warn(LocalFileClientService.className, `Unable to read file: ${filePath}, error: ${convertObjectToString(error)}`);
                    reject(error);
                    return;
                }
                resolve(data);
            });
        });
    }

    private async _readFileAsResource(filePath: string, isAbsolutePath: boolean): Promise<ITagResult> {
        return new Promise(async (resolve) => {
            const data = await this.getFileContent(filePath, isAbsolutePath);
            const resourceBase = this._localFileNameToResource(filePath);

            let result: ITagResult;
            if (resourceBase) {
                result = resourceBase as ITagResult;
                result.payload = data;
            } else {
                result = {
                    name: '',
                    type: '',
                    payload: data
                };
            }
            resolve(result);
        });
    }

    private _localFileNameToResource(fileName: string): IResourceBase | false {
        const file = this._translateLocalPathToWebPath(fileName.substr(this._rootFolder.length));
        return ResourceFilesUtils.filenameToResource(file);
    }

    private _translateLocalPathToWebPath(filePath: string): string {
        return filePath.replace(/\\/g, '/');
    }

    private async _getFolderFilesPath(folderPath: string): Promise<string[]> {
        const result: string[] = [];
        const dirents = (await promisify(readdir)(folderPath, { encoding: 'utf8', withFileTypes: true }) || []);
        for (const dirent of dirents) {
            const fullPath = normalizePath(joinPaths(folderPath, dirent.name));
            if (dirent.isDirectory()) {
                const nestedFiles = await this._getFolderFilesPath(fullPath);
                result.push(...nestedFiles);
            } else {
                result.push(fullPath);
            }
        }
        return result;
    }
}
